import React, { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import LinearProgress from '@mui/material/LinearProgress';
import { useNavigate, useParams } from 'react-router-dom';
import { NavModes } from '@/nav/utils/navigation.utils';
import { employerRefApi } from '@/employer-ref/services/employerRefApi';
import SectionLoader from '@/components/Loader/SectionLoader';
import useAlert from '@/hooks/useAlert';
import {
  getBoardId,
  getIsUserBoardSelected,
  getIsUserLoginSet,
  getUserName,
} from '@/core/web-storage/webStorage.utils';
import tokens from '@/nav/tokens.json';
import { SCOPES } from '@/authorization/authorization.constants';
import AuthorizationGate, {
  hasPermission,
} from '@/authorization/AuthorizationGate/AuthorizationGate';
import ChangeBoardDialog from '@/nav/components/ChangeBoardDialog/ChangeBoardDialog';
import webStorage, { LOCAL_STORAGE_KEYS } from '@/core/web-storage/webStorage';
import { usersApi } from '@/users/services/usersApi';
import { formatDateInDateTimeFormat } from '@/common/utils/date.utils';
import coreTokens from '@/core/tokens.json';
import { stringToBase64 } from '@/common/utils/encryption.utils';
import { ROUTE_NAMES } from '@/core/routes/routeNames';

interface UserInfoProps {
  type: NavModes;
}

function UserInfo({ type }: UserInfoProps) {
  const userName = getUserName();
  const userBoard = getBoardId();
  const navigate = useNavigate();
  const params = useParams();
  const canChangeBoard = hasPermission([SCOPES.CHANGE_BOARD]);
  const [employerName, setEmployerName] = useState(tokens['EMPLOYER_REFERENCE_NOT_FOUND_ERROR']);
  const [userLastLogin, setUserLastLogin] = useState(tokens['USER_LOGIN_NOT_FOUND_ERROR']);
  const [
    getEmployerRefInfo,
    {
      data: employerReferenceData,
      isLoading: employerRefIsLoading,
      isSuccess: employerRefIsSuccess,
      isError: employerRefIsError,
      error: employerRefError,
    },
  ] = employerRefApi.useLazyGetEmployerRefInfoQuery();
  const [
    getUserLastLogin,
    {
      data: userLastLoginData,
      isLoading: userLastLoginIsLoading,
      isSuccess: userLastLoginIsSuccess,
      isError: userLastLoginIsError,
      error: userLastLoginError,
    },
  ] = usersApi.useLazyGetUserLastLoginQuery();
  const [
    updateUserLogin,
    {
      isLoading: upateUserLoginIsLoading,
      isSuccess: updateUserLoginIsSuccess,
      isError: updateUserLoginIsError,
      error: updateUserLoginError,
    },
  ] = usersApi.useUpdateUserLoginMutation();
  const [isChangeBoardDialogOpen, setIsChangeBoardDialogOpen] = useState(false);
  const [showClose, setShowClose] = useState(true);

  function reloadOrGotoDashboard(qparams: typeof params) {
    if ('employeeId' in qparams) {
      navigate(ROUTE_NAMES.DASHBOARD.DASHBOARD);
    } else {
      window.location.reload();
    }
  }

  useEffect(() => {
    if (canChangeBoard) {
      const listener = (event: StorageEvent) => {
        if (event.key === stringToBase64(LOCAL_STORAGE_KEYS.BOARD_ID)) {
          reloadOrGotoDashboard(params);
        }
      };
      window.addEventListener('storage', listener);
      return () => window.removeEventListener('storage', listener);
    }
  }, [params]);

  useEffect(() => {
    void onUserLogin();

    if (hasPermission([SCOPES.VIEW_BOARD]) && userBoard) {
      void getEmployerRefInfo();
    }
    if (canChangeBoard) {
      const isUserBoardSelected = getIsUserBoardSelected();
      if (!isUserBoardSelected) {
        setShowClose(false);
        setIsChangeBoardDialogOpen(true);
      }
    }
  }, []);

  useEffect(() => {
    const isUserLoginSet = getIsUserLoginSet();

    if (employerRefIsSuccess && employerReferenceData) {
      setEmployerName(employerReferenceData.employerName);
    }
    if (userLastLoginIsSuccess) {
      if (userLastLoginData) {
        setUserLastLogin(formatDateInDateTimeFormat(userLastLoginData));
      } else {
        setUserLastLogin(tokens['USER_FIRST_LOGIN']);
      }
    }
    if (updateUserLoginIsSuccess && !isUserLoginSet) {
      void getUserLastLogin();
      webStorage.local.setItem(LOCAL_STORAGE_KEYS.IS_USER_LOGIN_SET, 'true');
    }
  }, [
    employerRefIsSuccess,
    employerRefIsError,
    userLastLoginIsSuccess,
    userLastLoginIsError,
    updateUserLoginIsSuccess,
  ]);

  useAlert({
    app: {},
    custom: {
      description: getErrorMessage(),
    },
    mui: {
      severity: 'error',
    },
    dependencies: [employerRefIsError, userLastLoginIsError, updateUserLoginIsError],
    condition: () => employerRefIsError || userLastLoginIsError || updateUserLoginIsError,
  });

  function onUserLogin() {
    const isUserLoginSet = getIsUserLoginSet();
    if (!isUserLoginSet) {
      void updateUserLogin();
    } else {
      void getUserLastLogin();
    }
  }

  const onOpenChangeBoardDialog = () => {
    setIsChangeBoardDialogOpen(true);
  };

  const onCloseChangeBoardDialog = () => {
    setIsChangeBoardDialogOpen(false);
  };

  function getErrorMessage() {
    switch (true) {
      case employerRefIsError:
        return employerRefError as string;
      case userLastLoginIsError:
        return userLastLoginError as string;
      case updateUserLoginIsError:
        return updateUserLoginError as string;
      default:
        return coreTokens['GENERAL_ERROR'];
    }
  }

  return type === 'collapsed' ? (
    <Box pt={4} pl={4} pr={4} p={2} display="flex" flexDirection="column" alignItems="center">
      <Avatar
        data-testid="user-avatar"
        variant="circular"
        sx={(theme) => ({
          backgroundColor: theme.palette.primary.main,
        })}
      >
        <SchoolOutlinedIcon fontSize="medium" display="block" />
      </Avatar>
    </Box>
  ) : (
    <>
      {employerRefIsLoading ? (
        <Box p={8} alignItems="center">
          <SectionLoader />
        </Box>
      ) : (
        <Box pt={4} pl={4} pr={4} display="flex" flexDirection="row" alignItems="center" gap={4}>
          <Avatar
            data-testid="user-avatar"
            variant="circular"
            sx={(theme) => ({
              backgroundColor: theme.palette.primary.main,
            })}
          >
            <SchoolOutlinedIcon fontSize="medium" display="block" />
          </Avatar>
          <Box display="flex" flexDirection="column">
            <Typography fontSize={14} sx={{ wordBreak: 'break-word' }}>
              {userName}
            </Typography>
            <AuthorizationGate scopes={[SCOPES.VIEW_BOARD]}>
              {userBoard ? (
                <Typography
                  fontSize={12}
                  color={employerRefIsError ? 'error' : 'secondary'}
                  sx={{
                    maxHeight: '60px',
                    overflowY: 'auto',
                  }}
                >
                  {employerName}
                </Typography>
              ) : null}
            </AuthorizationGate>
            <Typography fontSize={12} color="primary" sx={{ wordBreak: 'break-word' }}>
              Last Login:
            </Typography>
            {upateUserLoginIsLoading || userLastLoginIsLoading ? (
              <LinearProgress color="primary" variant="indeterminate" />
            ) : (
              <Typography
                fontSize={12}
                color={userLastLoginIsError || updateUserLoginIsError ? 'error' : 'primary'}
                sx={{ wordBreak: 'break-word' }}
              >
                {userLastLogin}
              </Typography>
            )}
            <AuthorizationGate scopes={[SCOPES.CHANGE_BOARD]}>
              <Button
                variant="outlined"
                size="small"
                role="button"
                sx={{ marginTop: 2, marginBottom: 2 }}
                onClick={onOpenChangeBoardDialog}
              >
                Change Board
              </Button>
              <ChangeBoardDialog
                currentBoardId={userBoard}
                open={isChangeBoardDialogOpen}
                onClose={onCloseChangeBoardDialog}
                showClose={showClose}
              />
            </AuthorizationGate>
          </Box>
        </Box>
      )}
    </>
  );
}

export default UserInfo;
